/**
 * Marketing Notification Service
 * Creates notifications for the marketing dashboard when application process events occur.
 * Collection: marketing_notifications
 * Payment notifications expire after 1 week; others after 30 days.
 */
import { getFunctions, httpsCallable } from "firebase/functions";

const COLLECTION = "marketing_notifications";
const COMPANY = "TPC_Group";
const COLLECTION_BRAND = "TechPerformanceCentre";

/** Expiry: payment = 7 days, others = 30 days */
const getExpiresAt = (type) => {
  const now = new Date();
  const days = type === "payment" ? 7 : 30;
  const expiresAt = new Date(now);
  expiresAt.setDate(expiresAt.getDate() + days);
  return expiresAt.toISOString();
};

/**
 * Create a marketing notification
 * @param {Object} params
 * @param {string} params.type - application_submitted | profile_completed | payment | document_uploaded
 * @param {string} params.title - Short title
 * @param {string} params.description - Main body / summary
 * @param {Object} [params.metadata] - Extra structured data (name, email, programme, etc.)
 */
export const createMarketingNotification = async ({
  type,
  title,
  description,
  metadata = {},
}) => {
  try {
    const functions = getFunctions();
    const firestoreOps = httpsCallable(functions, "firestoreOperations");
    const id = `mk_${type}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

    await firestoreOps({
      operation: "write",
      company: COMPANY,
      collectionBrand: COLLECTION_BRAND,
      systemName: COLLECTION,
      uniqueId: id,
      documentData: {
        id,
        type,
        title,
        description,
        metadata,
        read: false,
        timestamp: new Date().toISOString(),
        expiresAt: getExpiresAt(type),
      },
    });
  } catch (err) {
    console.error("[marketingNotificationService] Failed to create notification:", err);
  }
};

/**
 * Delete a marketing notification
 * @param {string} id - Notification id
 */
export const deleteMarketingNotification = async (id) => {
  try {
    const functions = getFunctions();
    const firestoreOps = httpsCallable(functions, "firestoreOperations");
    await firestoreOps({
      operation: "delete",
      company: COMPANY,
      collectionBrand: COLLECTION_BRAND,
      systemName: COLLECTION,
      uniqueId: id,
      auth: true,
    });
  } catch (err) {
    console.error("[marketingNotificationService] Failed to delete notification:", err);
    throw err;
  }
};

/**
 * Mark a marketing notification as read
 * @param {string} id - Notification id
 */
export const markMarketingNotificationRead = async (id) => {
  try {
    const functions = getFunctions();
    const firestoreOps = httpsCallable(functions, "firestoreOperations");
    await firestoreOps({
      operation: "update",
      company: COMPANY,
      collectionBrand: COLLECTION_BRAND,
      systemName: COLLECTION,
      uniqueId: id,
      documentData: { read: true },
      auth: true,
    });
  } catch (err) {
    console.error("[marketingNotificationService] Failed to mark read:", err);
  }
};
